import { FC, useState } from 'react';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FilterListIcon from '@mui/icons-material/FilterListTwoTone';
import {
    CardContent,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

import TableBackground from 'src/assets/images/backgrounds/Table.jpg';
import MainCard from 'src/components/MainCard/MainCard';
import { FilterOptionsType } from 'src/utils/tableUtils';

interface FilterSectionProps {
    filterOptions: FilterOptionsType;
    updateTableFilters: (filter: string, newValue: string) => void;
}

const FilterSection: FC<FilterSectionProps> = ({ filterOptions, updateTableFilters }) => {
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [selectedSection, setSelectedSection] = useState<string>('');
    const [selectedResource, setSelectedResource] = useState<string>('');

    const handleShowFilter = (): void => {
        setShowFilters(!showFilters);
        updateTableFilters('clear', '-');
    };

    const handleClearFilter = (filterName: string): void => {
        if (filterName === 'section') {
            setSelectedSection('');
        } else if (filterName === 'resourceName') {
            setSelectedResource('');
        }
        updateTableFilters(filterName, '');
    };

    const handleChangeSection = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSelectedSection(e.target.value);
        updateTableFilters('section', e.target.value);
    };

    const handleChangeResource = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSelectedResource(e.target.value);
        updateTableFilters('resourceName', e.target.value);
    };

    return (
        <MainCard content={false} sx={{ boxShadow: 'customShadows.tableListBoxShadow' }}>
            <CardContent
                sx={{
                    backgroundImage: `url(${TableBackground})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    borderRadius: '16px 0px',
                }}
            >
                <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            onChange={(event) => updateTableFilters('search', event.target.value)}
                            placeholder="Search All Resources"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderRadius: '4px',
                                    border: '1px solid',
                                    borderColor: 'border.light',
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Tooltip title="Filter">
                                <IconButton size="large" onClick={handleShowFilter} color="primary">
                                    <FilterListIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Grid>
                </Grid>

                {showFilters && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.primary' }}>
                                Section
                            </Typography>
                            <TextField
                                select
                                value={selectedSection}
                                onChange={handleChangeSection}
                                fullWidth
                                size="small"
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderRadius: '4px',
                                        border: '1px solid',
                                        borderColor: 'border.primary',
                                    },
                                }}
                                InputProps={{
                                    endAdornment: selectedSection && (
                                        <InputAdornment position="end" sx={{ paddingRight: '8px' }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleClearFilter('section')}
                                            >
                                                <CloseOutlinedIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            >
                                {filterOptions['Section'].map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.primary' }}>
                                Resource Name
                            </Typography>
                            <TextField
                                select
                                value={selectedResource}
                                onChange={handleChangeResource}
                                fullWidth
                                size="small"
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderRadius: '4px',
                                        border: '1px solid',
                                        borderColor: 'border.primary',
                                    },
                                }}
                                InputProps={{
                                    endAdornment: selectedResource && (
                                        <InputAdornment position="end" sx={{ paddingRight: '8px' }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleClearFilter('resourceName')}
                                            >
                                                <CloseOutlinedIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            >
                                {filterOptions['Resource Name'].map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <IconButton
                                onClick={handleShowFilter}
                                color="secondary"
                                sx={{ color: 'border.light' }}
                            >
                                <CloseOutlinedIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                )}
            </CardContent>
        </MainCard>
    );
};

export default FilterSection;
